import { Award, Cpu, Truck, DollarSign, Target, Eye, ArrowRight } from "lucide-react";
import { useScrollReveal } from "../hooks/useAnimations";
import { assets } from "../lib/assets";

const partnerIcons = [Award, Cpu, Truck, DollarSign];

export default function WhyPartner({ t }) {
  const [headerRef, headerVis] = useScrollReveal();
  const [gridRef, gridVis] = useScrollReveal({ threshold: 0.1 });
  const [visionRef, visionVis] = useScrollReveal({ threshold: 0.15 });

  return (
    <section id="partners" data-testid="partners-section" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`reveal ${headerVis ? 'visible' : ''}`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-[2px] bg-brand-blue" />
            <span className="text-brand-blue text-xs font-semibold tracking-[0.25em] uppercase">
              {t.partners.sectionLabel}
            </span>
          </div>
          <h2
            data-testid="partners-title"
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-[3.2rem] text-brand-blue leading-[1.08] tracking-tight mb-14"
          >
            {t.partners.title}
          </h2>
        </div>

        <div ref={gridRef} className={`grid lg:grid-cols-12 gap-3 mb-16 stagger-children ${gridVis ? 'visible' : ''}`}>
          {t.partners.items.map((item, i) => {
            const Icon = partnerIcons[i];
            const isWide = i === 0 || i === 3;
            return (
              <div
                key={i}
                data-testid={`partner-card-${i}`}
                className={`${isWide ? 'lg:col-span-7' : 'lg:col-span-5'} ${i >= 2 ? (i === 2 ? 'lg:col-span-5' : 'lg:col-span-7') : ''} rounded-2xl border border-brand-blue/20 bg-card-mint p-7 hover-lift hover-glow group cursor-default relative overflow-hidden`}
              >
                <div className="absolute top-3 right-5 font-heading font-bold text-[80px] text-brand-blue/25 leading-none select-none pointer-events-none transition-colors duration-500 group-hover:text-brand-blue/35">
                  {String(i + 1).padStart(2, '0')}
                </div>

                <div className="relative z-10">
                  <div className="w-11 h-11 rounded-xl bg-brand-blue flex items-center justify-center mb-5 transition-all duration-400">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2 text-[#333333] group-hover:text-brand-blue transition-colors">{item.title}</h3>
                  <p className="text-slate-600 text-base leading-relaxed max-w-sm">{item.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div ref={visionRef} className={`reveal ${visionVis ? 'visible' : ''}`}>
          <div className="rounded-2xl overflow-hidden relative group mb-3">
            <div className="aspect-video">
              <img src={assets.whyPartnerFactory} alt="Manufacturing facility" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/60 to-transparent" />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="bg-emerald-900 rounded-2xl p-6 lg:p-7 text-emerald-50 relative overflow-hidden noise-overlay hover-lift group">
              <div className="relative z-10 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-accent-green/20 flex items-center justify-center flex-shrink-0 group-hover:bg-accent-green/30 transition-colors">
                  <Target className="w-4 h-4 text-accent-green" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg mb-2">{t.vision.mission}</h3>
                  <p className="text-emerald-100/90 text-base leading-relaxed">{t.vision.missionText}</p>
                </div>
              </div>
            </div>
            <div className="bg-card-mint border border-brand-blue/20 rounded-2xl p-6 lg:p-7 hover-lift hover-glow group">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-accent-green flex items-center justify-center flex-shrink-0 transition-colors">
                  <Eye className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-[#333333] text-lg mb-2">{t.vision.vision}</h3>
                  <p className="text-slate-600 text-base leading-relaxed">{t.vision.visionText}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
